import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, authFetch } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { PageSEO } from '../components/PageSEO';
import { RegisterModal } from '../components/RegisterModal';
import { ToolFeedback } from '../components/ToolFeedback';
import { EmailVerificationWall } from '../components/EmailVerificationWall';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { PositionCombobox } from '../components/PositionCombobox';
import { toast } from 'sonner';
import {
  BarChart3,
  Loader2,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  ShieldAlert,
  BadgeCheck,
  Lock,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const industries = [
  { value: 'tecnologia', label: 'Tecnología' },
  { value: 'finanzas', label: 'Finanzas' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'recursos_humanos', label: 'Recursos Humanos' },
  { value: 'ventas', label: 'Ventas' },
  { value: 'operaciones', label: 'Operaciones' },
  { value: 'legal', label: 'Legal' },
  { value: 'otro', label: 'Otro' },
];

const seniorities = [
  { value: 'junior', label: 'Junior (0-2 años)' },
  { value: 'mid', label: 'Mid-Level (3-5 años)' },
  { value: 'senior', label: 'Senior (6-10 años)' },
  { value: 'lead', label: 'Lead / Gerente (10+ años)' },
];

const geographicZones = [
  { value: 'general', label: 'Zona General' },
  { value: 'frontera_norte', label: 'Zona Libre de la Frontera Norte' },
];

// No confiar en blur() de CSS para ocultar el valor real: en Liquidación un número grande
// y en negrita sobre fondo oscuro seguía siendo legible a simple vista con blur-sm (bug
// real en prod, sept. 2026). Se sustituye el valor por un marcador fijo en vez de
// difuminar el real — no hay dígitos ni texto real en el DOM que blurear. La barra de
// progreso tiene el mismo problema por otra vía: aunque el número se oculte, el largo del
// relleno ya revela el percentil real incluso con blur, así que se fija en un valor
// decorativo cuando está bloqueada, en vez de reflejar el dato real.
const MASKED_CURRENCY = '$•••,•••';
const MASKED_PERCENT = '••%';
const MASKED_TEXT = '••••••••';
const MASKED_PARAGRAPH = '•••••••• •••• •••••••• ••• ••••••••• •• •••••••.';
const DECORATIVE_PROGRESS_VALUE = 50;

export default function SalaryBenchmarkPage() {
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const pendingSubmitRef = useRef(false);

  const [formData, setFormData] = useState({
    role: '',
    industry: '',
    city: '',
    seniority: '',
    salary: '',
    geographic_zone: 'general',
  });

  const isPro = user?.plan === 'pro';
  const isBlurred = !!result && !isPro;

  // Registra en usage_limit_events cada vez que un usuario no-Pro ve la versión
  // difuminada del resultado (reemplaza a "limit_reached" para esta herramienta, que ya
  // no aplica: no hay límite de consultas, el difuminado es el mecanismo permanente).
  useEffect(() => {
    if (isBlurred) {
      authFetch(`${API_URL}/api/usage-limit/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tool: 'salary_benchmark', event: 'blurred_result_view' }),
      }).catch(() => { /* non-blocking */ });
    }
  }, [result, isPro]);

  const trackUpgradeClick = () => {
    authFetch(`${API_URL}/api/usage-limit/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tool: 'salary_benchmark', event: 'upgrade_click' }),
    }).catch(() => { /* non-blocking */ });
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const canSubmit = () => {
    return formData.role && formData.industry && formData.city && formData.seniority && formData.salary;
  };

  const executeSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        salary: parseFloat(formData.salary),
      };

      const response = await authFetch(`${API_URL}/api/benchmarks/salary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Error al calcular benchmark');
      }

      const data = await response.json();
      setResult(data);
      toast.success('Benchmark calculado');
    } catch (error) {
      toast.error(error.message || 'Error al calcular');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit()) return;

    if (!isAuthenticated) {
      pendingSubmitRef.current = true;
      setShowRegisterModal(true);
      return;
    }

    await executeSubmit();
  };

  const handleRegisterSuccess = (registeredUser) => {
    if (pendingSubmitRef.current) {
      pendingSubmitRef.current = false;
      // Cuenta recién creada, aún sin verificar: el muro se mostrará en el siguiente
      // render en vez de ejecutar la consulta pendiente.
      if (registeredUser?.email_verified === false) return;
      executeSubmit();
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusIcon = () => {
    if (!result) return null;
    if (result.percentile >= 75) return <TrendingUp className="w-5 h-5 text-[#10B981]" />;
    if (result.percentile >= 50) return <Minus className="w-5 h-5 text-[#F59E0B]" />;
    return <TrendingDown className="w-5 h-5 text-[#EF4444]" />;
  };

  const getStatusColor = () => {
    if (!result) return '';
    if (result.percentile >= 75) return 'text-[#10B981]';
    if (result.percentile >= 50) return 'text-[#F59E0B]';
    return 'text-[#EF4444]';
  };

  const getProgressColor = () => {
    if (!result) return 'bg-muted';
    if (result.percentile >= 75) return 'bg-[#10B981]';
    if (result.percentile >= 50) return 'bg-[#F59E0B]';
    return 'bg-[#EF4444]';
  };

  return (
    <Layout>
      <PageSEO
        title="¿Cuánto Debería Ganar? Benchmark Salarial México | Klarvi"
        description="Descubre cuánto debería ganar según tu puesto, industria y experiencia en México. Compara tu sueldo contra el mercado con datos reales."
        canonical="https://klarvi.org/salary-benchmark"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Benchmark Salarial - Klarvi",
          "url": "https://klarvi.org/salary-benchmark",
          "description": "Compara tu sueldo contra el mercado mexicano según puesto, industria, ciudad y experiencia.",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "inLanguage": "es-MX",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "MXN" },
        }}
      />
      {isAuthenticated && user?.email_verified === false ? (
        <EmailVerificationWall />
      ) : (
      <>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="salary-benchmark-page">
        <div>
          <h1 className="text-3xl font-bold text-[#0F172A] font-['Manrope']">
            Benchmark Salarial
          </h1>
          <p className="mt-2 text-muted-foreground">
            Compara tu salario con el mercado mexicano para tu industria y nivel.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <Card className="border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Manrope']">
                <BarChart3 className="w-5 h-5 text-[#2563EB]" />
                Tu Información
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="role">Puesto</Label>
                  <PositionCombobox
                    value={formData.role}
                    onChange={(v) => updateField('role', v)}
                    placeholder="Ej: Product Manager"
                    testId="benchmark-role-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Los puestos marcados con <BadgeCheck className="w-3 h-3 inline text-teal-600" /> tienen banda salarial real de la Guía Salarial Buk 2026.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="industry">Industria</Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(v) => updateField('industry', v)}
                  >
                    <SelectTrigger data-testid="benchmark-industry-select">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((ind) => (
                        <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">Ciudad</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => updateField('city', e.target.value)}
                    placeholder="Ej: Ciudad de México"
                    data-testid="benchmark-city-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="seniority">Nivel de Experiencia</Label>
                  <Select
                    value={formData.seniority}
                    onValueChange={(v) => updateField('seniority', v)}
                  >
                    <SelectTrigger data-testid="benchmark-seniority-select">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {seniorities.map((sen) => (
                        <SelectItem key={sen.value} value={sen.value}>{sen.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="salary">Tu Salario Mensual (MXN)</Label>
                  <Input
                    id="salary"
                    type="number"
                    value={formData.salary}
                    onChange={(e) => updateField('salary', e.target.value)}
                    placeholder="45000"
                    data-testid="benchmark-salary-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="geographic_zone">Zona Geográfica</Label>
                  <Select
                    value={formData.geographic_zone}
                    onValueChange={(v) => updateField('geographic_zone', v)}
                  >
                    <SelectTrigger data-testid="benchmark-zone-select">
                      <SelectValue placeholder="Seleccionar zona" />
                    </SelectTrigger>
                    <SelectContent>
                      {geographicZones.map((z) => (
                        <SelectItem key={z.value} value={z.value}>{z.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#2563EB] hover:bg-[#2563EB]/90"
                  disabled={!canSubmit() || loading}
                  data-testid="benchmark-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Comparar
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          <div className="space-y-4">
            {result ? (
              <>
                {/* Legal Alert */}
                {result.legal_alert && (
                  <Card className="border-2 border-red-400 bg-red-50 animate-scale-in" data-testid="legal-alert-banner">
                    <CardContent className="p-4 flex items-start gap-3">
                      <ShieldAlert className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-red-800">Alerta: posible incumplimiento legal</p>
                        <p className="text-sm text-red-700 mt-1">{result.legal_alert.message}</p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Percentile Card */}
                <Card className="border animate-scale-in">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-[#0F172A] font-['Manrope']">
                          Tu Posición en el Mercado
                        </h3>
                        <p className={`text-sm mt-1 flex items-center gap-1 ${getStatusColor()}`}>
                          {getStatusIcon()}
                          {isBlurred ? MASKED_TEXT : result.status}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-[#0F172A]">
                          {isBlurred ? MASKED_PERCENT : `${result.percentile}%`}
                        </p>
                        <p className="text-sm text-muted-foreground">Percentil</p>
                      </div>
                    </div>
                    <div className="relative">
                      <Progress value={isBlurred ? DECORATIVE_PROGRESS_VALUE : result.percentile} className="h-3" />
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-1 h-5 bg-[#0F172A] rounded"
                        style={{ left: `${isBlurred ? DECORATIVE_PROGRESS_VALUE : result.percentile}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground mt-2">
                      <span>0%</span>
                      <span>25%</span>
                      <span>50%</span>
                      <span>75%</span>
                      <span>100%</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 pt-3 border-t">
                      Ganas más que el {isBlurred ? MASKED_PERCENT : `${result.percentile}%`} de las personas con un puesto similar al tuyo en tu zona.
                    </p>
                  </CardContent>
                </Card>

                {/* Market Range */}
                <Card className="border animate-slide-up" style={{ animationDelay: '100ms' }}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-['Manrope']">
                      Rango de Mercado
                    </CardTitle>
                    {result.matched_position ? (
                      <p className="text-sm text-teal-700 flex items-center gap-1.5" data-testid="matched-position-info">
                        <BadgeCheck className="w-4 h-4 flex-shrink-0" />
                        Comparado contra <strong>{result.matched_position}</strong> — Guía Salarial Buk 2026
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground" data-testid="estimate-disclaimer">
                        {result.data_source || 'Estimación de mercado'}
                      </p>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-sm text-muted-foreground">Percentil 25</span>
                        <span className="font-medium">{isBlurred ? MASKED_CURRENCY : formatCurrency(result.market_range.p25)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b bg-muted/50 -mx-4 px-4">
                        <span className="text-sm font-medium text-[#0F172A]">Mediana</span>
                        <span className="font-bold text-[#0F172A]">{isBlurred ? MASKED_CURRENCY : formatCurrency(result.market_range.median)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-muted-foreground">Percentil 75</span>
                        <span className="font-medium">{isBlurred ? MASKED_CURRENCY : formatCurrency(result.market_range.p75)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Your Salary */}
                <Card className="border bg-[#2563EB]/5 animate-slide-up" style={{ animationDelay: '150ms' }}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#2563EB]">Tu salario</span>
                      <span className="font-bold text-[#2563EB] text-lg">
                        {formatCurrency(parseFloat(formData.salary))}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* AI Summary */}
                {result.resumen && (
                  <Card className="border animate-slide-up" style={{ animationDelay: '200ms' }}>
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground" data-testid="benchmark-resumen">
                        {isBlurred ? MASKED_PARAGRAPH : result.resumen}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Pro upsell — reemplaza el límite de usos para esta herramienta */}
                {isBlurred && (
                  <div className="flex flex-wrap items-center justify-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="benchmark-pro-upsell">
                    <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span>Con Pro ves exactamente dónde te ubicas frente al mercado y el rango completo de sueldos — sin límite de consultas.</span>
                    <Link to="/precios" onClick={trackUpgradeClick}>
                      <Button size="sm" className="bg-[#2563EB] hover:bg-[#2563EB]/90" data-testid="benchmark-upgrade-btn">
                        Actualizar a Pro
                      </Button>
                    </Link>
                  </div>
                )}

                {/* Next step */}
                <Card className="border-[#2563EB]/20 bg-[#2563EB]/5 animate-slide-up" style={{ animationDelay: '250ms' }}>
                  <CardContent className="p-4 text-sm">
                    <p className="font-medium text-[#0F172A] mb-1">¿Y ahora qué?</p>
                    <p className="text-muted-foreground">
                      Si estás por debajo del mercado, esto te sirve para pedir un ajuste. Llévale este comparativo al{' '}
                      <Link to="/advisor" className="text-[#2563EB] hover:underline font-medium">
                        Asesor IA
                      </Link>{' '}
                      y prepara qué decir antes de hablar con tu jefe o RRHH.
                    </p>
                  </CardContent>
                </Card>

                {/* Disclaimer */}
                <div className="legal-disclaimer flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Rangos estimados basados en referencias de mercado. Los datos pueden variar según empresa, beneficios y negociación individual.
                  </span>
                </div>
                <ToolFeedback tool="benchmark_salarial" />
              </>
            ) : (
              <Card className="border h-full min-h-[400px] flex items-center justify-center">
                <CardContent className="text-center">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                    <BarChart3 className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-[#0F172A] font-['Manrope']">
                    Compara tu salario
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Ingresa tu información para ver cómo se compara tu salario con el mercado.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      <RegisterModal
        open={showRegisterModal}
        onOpenChange={setShowRegisterModal}
        onSuccess={handleRegisterSuccess}
      />
      </>
      )}
    </Layout>
  );
}
